import { useEffect, useState, type FormEvent } from 'react';
import { Carte, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import {
  useReglerAbsenteisme,
  useReglerSeuilFormation,
  type SeuilFormation,
  type VueParametrage,
} from '../../lib/useParametrage';
import {
  CLASSE_BOUTON_ENREGISTRER,
  CLASSE_CHAMP_NOMBRE,
  mentionReglage,
  messageErreur,
} from './commun';
import { SectionReglage } from './SectionReglage';

/**
 * Deux seuils, qui ne répondent pas à la même question (TF-427, TF-435) :
 * l'absentéisme de l'organisme signale un apprenant à risque à la direction ;
 * le seuil de chaque formation, en demi-journées non justifiées, déclenche le
 * courrier d'avertissement par palier.
 */
export function OngletAbsences({ absences }: { absences: VueParametrage['absences'] }): JSX.Element {
  return (
    <div className="space-y-6">
      <SeuilAbsenteisme absences={absences} />
      <Carte titre="Seuil du courrier d’absences, par formation">
        <p className="px-4 py-3 text-xs text-codialis-gray sm:px-6">
          Le courrier d’avertissement part quand les absences définitives non justifiées d’un apprenant atteignent ce
          seuil, puis à chaque multiple du seuil. Un nouveau seuil vaut pour les paliers à venir : un courrier déjà parti
          reste. Par défaut, {absences.seuilParDefaut} demi-journées.
        </p>
        {absences.formations.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-codialis-gray sm:px-6">Aucune formation au catalogue.</p>
        )}
        {absences.formations.map((formation) => (
          <LigneSeuilFormation key={formation.id} formation={formation} min={absences.min} max={absences.max} />
        ))}
      </Carte>
    </div>
  );
}

function SeuilAbsenteisme({ absences }: { absences: VueParametrage['absences'] }): JSX.Element {
  const toast = useToast();
  const regler = useReglerAbsenteisme();
  const seuil = absences.absenteisme;
  const [saisie, setSaisie] = useState(String(seuil.valeur));
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => setSaisie(String(seuil.valeur)), [seuil.valeur]);

  const valeur = Number(saisie);
  const inchange = saisie.trim() === '' || valeur === seuil.valeur;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(valeur, {
      onSuccess: (nouveau) => toast(`Seuil d’absentéisme réglé à ${nouveau.valeur} %`),
      onError: (refus) => setErreur(messageErreur(refus, 'Le seuil n’a pas pu être enregistré.')),
    });
  };

  return (
    <SectionReglage
      titre="Seuil d’absentéisme de l’organisme"
      saisie={
        <form onSubmit={enregistrer} className="flex flex-col gap-2 sm:items-end">
          <div className="flex items-center gap-2">
            <label htmlFor="parametrage-absenteisme" className="text-sm font-medium text-codialis-navy">
              Au-delà de
            </label>
            <div className="relative">
              <input
                id="parametrage-absenteisme"
                type="number"
                inputMode="numeric"
                min={seuil.min}
                max={seuil.max}
                step={1}
                required
                value={saisie}
                onChange={(evenement) => setSaisie(evenement.target.value)}
                className={`${CLASSE_CHAMP_NOMBRE} pr-8`}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-codialis-gray">
                %
              </span>
            </div>
            <button type="submit" disabled={inchange || regler.isPending} className={CLASSE_BOUTON_ENREGISTRER}>
              {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
          {erreur && (
            <p role="alert" className="text-xs font-medium text-red-600">
              {erreur}
            </p>
          )}
        </form>
      }
    >
      <p className="text-xs text-codialis-gray">
        Un apprenant en formation est signalé « à risque » à la direction quand ses absences — justifiées comprises —
        dépassent ce pourcentage des demi-journées clôturées depuis le début de son parcours. C’est le même réglage que
        dans « Apprenants à risque ».
      </p>
      <p className="text-[11px] text-codialis-gray">{mentionReglage(seuil)}</p>
    </SectionReglage>
  );
}

function LigneSeuilFormation({
  formation,
  min,
  max,
}: {
  formation: SeuilFormation;
  min: number;
  max: number;
}): JSX.Element {
  const toast = useToast();
  const regler = useReglerSeuilFormation();
  const [saisie, setSaisie] = useState(String(formation.seuil));
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => setSaisie(String(formation.seuil)), [formation.seuil]);

  const valeur = Number(saisie);
  const inchange = saisie.trim() === '' || valeur === formation.seuil;
  const champ = `seuil-formation-${formation.id}`;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(
      { formationId: formation.id, seuil: valeur },
      {
        onSuccess: () => toast(`${formation.titre} : courrier à ${valeur} demi-journée${valeur > 1 ? 's' : ''}`),
        onError: (refus) => setErreur(messageErreur(refus, 'Le seuil n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <form
      onSubmit={enregistrer}
      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
    >
      <div className="min-w-0">
        <label htmlFor={champ} className="text-sm font-semibold text-codialis-navy">
          {formation.titre}
        </label>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-codialis-gray">
          <span>{formation.code}</span>
          <span>·</span>
          <span>
            {formation.dossiersSuivis} dossier{formation.dossiersSuivis > 1 ? 's' : ''} concerné
            {formation.dossiersSuivis > 1 ? 's' : ''}
          </span>
          {!formation.actif && <Pastille texte="Inactive" ton="neutral" />}
        </div>
        {erreur && (
          <p role="alert" className="mt-1 text-xs font-medium text-red-600">
            {erreur}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <input
          id={champ}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          required
          value={saisie}
          onChange={(evenement) => setSaisie(evenement.target.value)}
          className={CLASSE_CHAMP_NOMBRE}
        />
        <span className="text-xs text-codialis-gray">demi-journées</span>
        <button type="submit" disabled={inchange || regler.isPending} className={CLASSE_BOUTON_ENREGISTRER}>
          {regler.isPending ? '…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
